import { formatAccounting } from './boardPackHelpers';
import { screenForRollup } from './boardScreenHelpers';

const short = (id) => String(id || '').slice(0, 6);

export const formatEvidenceDate = (value) => {
  if (!value) return '';
  const text = String(value).slice(0, 10);
  if (/^\d{4}-\d{2}-\d{2}$/.test(text)) return text;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return text;
  return parsed.toISOString().slice(0, 10);
};

export const componentTypeLabel = (componentType) => {
  const value = String(componentType || '').trim();
  if (!value) return 'component';
  return value.replace(/_/g, ' ');
};

const formatExpectationBand = (exp = {}) => {
  const unit = exp.unit || exp.metric_unit;
  const target = exp.targetValue ?? exp.target_value;
  const lo = exp.toleranceLow ?? exp.tolerance_low;
  const hi = exp.toleranceHigh ?? exp.tolerance_high;
  const fmt = (n) => {
    if (n === null || n === undefined) return '—';
    if (unit === 'ratio' || unit === 'pct') return `${(Number(n) * 100).toFixed(2)}%`; // both stored as decimal fractions
    return formatAccounting(n);
  };
  return `target ${fmt(target)} · band ${fmt(lo)}–${fmt(hi)}`;
};

const basisLabel = (basis) =>
  String(basis || 'forecast').replace(/_/g, ' ');

export const describeEvidenceRow = (
  type,
  id,
  {
    evidence = {},
    findingsById = {},
    expectationsByKey = {}
  } = {}
) => {
  if (type === 'finding') {
    const finding = findingsById[id] || evidence?.findings?.[id];
    if (finding) {
      return {
        label: `${finding.planningEntity || 'entity n/a'} · ${finding.glGroup || 'GL group'} · score ${Number(finding.score || 0).toFixed(1)}`,
        detail: finding.dollarImpact !== undefined ? formatAccounting(finding.dollarImpact) : null,
        onClick: null,
        finding
      };
    }
    return { label: `Finding ${short(id)}` };
  }

  if (type === 'bridge') {
    const bridge = evidence?.bridges?.[id];
    if (bridge) {
      const glGroup = bridge.glGroup || bridge.gl_group || 'GL group';
      const entity = bridge.planningEntity || bridge.planning_entity || 'company';
      const compLabel = componentTypeLabel(bridge.componentType || bridge.component_type || bridge.label);
      const amount = formatAccounting(bridge.amount ?? 0);
      return {
        label: `${glGroup} · ${entity} · ${compLabel} ${amount}`,
        bridgeInfo: {
          id,
          glGroup,
          planningEntity: entity,
          rollup: bridge.rollup,
          screenId: screenForRollup(bridge.rollup),
          lineKey: `${entity}::${glGroup}`
        },
        clickable: true
      };
    }
    return { label: `Bridge decomposition ${short(id)}` };
  }

  if (type === 'comment') {
    const comment = evidence?.comments?.[id];
    if (comment) {
      const author = comment.author || 'analyst';
      const date = formatEvidenceDate(comment.createdAt || comment.created_at);
      return {
        header: `${author}${date ? ` · ${date}` : ''}`,
        body: comment.body || '',
        expandable: Boolean(comment.body)
      };
    }
    return { label: 'Analyst comment' };
  }

  if (type === 'disposition') {
    const disposition = evidence?.dispositions?.[id];
    if (disposition) {
      const verdict = String(disposition.verdict || 'disposition').replace(/_/g, ' ');
      const createdBy = disposition.createdBy || disposition.created_by || 'analyst';
      const date = formatEvidenceDate(disposition.createdAt || disposition.created_at);
      const finding = disposition.findingId
        ? (findingsById[disposition.findingId] || evidence?.findings?.[disposition.findingId])
        : Object.values({ ...(evidence?.findings || {}), ...(findingsById || {}) }).find((f) => f?.disposition?.id === id);
      return {
        header: `${verdict} · ${createdBy}${date ? ` · ${date}` : ''}`,
        body: disposition.note || '',
        expandable: Boolean(disposition.note),
        finding
      };
    }
    const owner = Object.values({ ...(evidence?.findings || {}), ...(findingsById || {}) }).find((f) => f?.disposition?.id === id);
    if (owner?.disposition) {
      const verdict = String(owner.disposition.verdict || 'disposition').replace(/_/g, ' ');
      return {
        header: `${verdict} · ${owner.disposition.createdBy || 'analyst'}`,
        body: owner.disposition.note || '',
        expandable: Boolean(owner.disposition.note),
        finding: owner
      };
    }
    return { label: 'Analyst disposition' };
  }

  if (type === 'expectation') {
    const exp = evidence?.expectations?.[id] || expectationsByKey[id];
    if (exp) {
      const label = exp.metricLabel || exp.metric_label || `Expectation ${short(id)}`;
      return {
        label,
        detail: formatExpectationBand(exp)
      };
    }
    return { label: `Expectation ${short(id)}` };
  }

  if (type === 'forecastContext') {
    const row = evidence?.forecastContexts?.[id];
    if (row) {
      const metric = row.glGroup || row.metricLabel || 'Forecast basis';
      const entity = row.planningEntity || row.planning_entity || row.lob || 'scope';
      const basis = basisLabel(row.basis);
      const source = formatAccounting(row.sourceValue ?? row.source_value ?? 0);
      const actual = formatAccounting(row.actualValue ?? row.actual_value ?? 0);
      const variance = formatAccounting(row.varianceValue ?? row.variance_value ?? 0);
      return {
        label: `${metric} · ${entity} · ${basis} basis`,
        detail: `forecast ${source} · actual ${actual} · variance ${variance}`
      };
    }
    return { label: `Forecast basis ${short(id)}` };
  }

  if (type === 'forecastOverride') {
    const row = evidence?.forecastOverrides?.[id];
    if (row) {
      const metric = row.metricLabel || row.metric_label || row.metricKey || row.metric_key || 'Forecast override';
      const entity = row.planningEntity || row.planning_entity || row.lob || 'scope';
      const override = formatAccounting(row.overrideValue ?? row.override_value ?? 0);
      const source = formatAccounting(row.sourceValue ?? row.source_value ?? 0);
      const date = formatEvidenceDate(row.createdAt || row.created_at);
      const actor = row.createdBy || row.created_by || 'analyst';
      return {
        label: `${metric} · ${entity}`,
        detail: `override ${override} vs source ${source}${row.note ? ` · ${row.note}` : ''} · ${actor}${date ? ` · ${date}` : ''}`
      };
    }
    return { label: `Forecast override ${short(id)}` };
  }

  return { label: `${type} ${short(id)}` };
};
